import { RouterProvider } from '@tanstack/react-router'
import { useAuthStore } from '@zealth/core/firebase/authStore'
import { initFirebase } from '@zealth/core/firebase/runtime'

import firebaseConfig from '@/config/firebase'
import { Sentry } from '@/config/sentry'
import { AnalyticsProvider } from '@/features/analytics'
import { FlashProvider } from '@/features/flash'

import ErrorState from './features/feedback/ErrorState'
import { useImpersonationListener } from './features/impersonation/useImpersonationListener'
import { useSupportWidget } from './features/support/useSupportWidget'
import { isUserScopedQuery, queryClient } from './queryClient'
import { router } from './router'

initFirebase(firebaseConfig)

// Re-run route beforeLoad guards whenever the signed-in user identity changes.
// Covers same-tab sign-in (email-link finish, password flow), cross-tab sign-in
// (link opened in a new tab), and sign-out from any tab — Firebase syncs auth
// across tabs via IndexedDB, so authStore.user flips in every tab automatically.
//
// `resetQueries` (not `invalidateQueries`) on auth change: collections under
// `authenticated: true` in firestoreApi are keyed by document id only — not
// uid — so the previous user's data would otherwise stay in the cache. The
// default stale-while-revalidate keeps that previous data on observers during
// the refetch, which means the dashboard would flash user A's profile/surveys
// for a beat after switching to user B. `resetQueries` drops the cached data,
// puts active observers back into a pending state, and triggers a fresh
// fetch — clean loading instead of a stale flash.
useAuthStore.subscribe(
  state => state.user?.uid,
  () => {
    queryClient.resetQueries({ predicate: isUserScopedQuery })
    router.invalidate()
  }
)

const App = () => {
  useImpersonationListener()
  useSupportWidget()

  return (
    <Sentry.ErrorBoundary
      fallback={({ error }) => <ErrorState error={error} />}
    >
      <AnalyticsProvider>
        <FlashProvider>
          <RouterProvider router={router} />
        </FlashProvider>
      </AnalyticsProvider>
    </Sentry.ErrorBoundary>
  )
}

export default App
